import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  AuthenticationFlowType,
  DomainPolicy,
} from '../../../../../typings/api/skymap/rest/v1/.common';
import {
  buildInvitationUrl,
  startInvitationAuthorizationRequest,
} from '../../../services/authentication/zitadel-invitation';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { usePasswordConstraints } from '../../hooks/use-password-constraints';
import { useRouter } from '../../hooks/use-router';
import { Button } from '../button/button';
import { InfoBox } from '../info-box/info-box';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { ChooseAuthentication } from '../new-company-admin/forms/choose-authentication';
import { isEmptyField } from '../register-company/utils';
import { TextLoading } from '../skeleton';
import { Stack } from '../stack/stack';
import { TermsOfServiceCheckBox } from '../terms-of-service-check-box/terms-of-service-check-box';
import { TextBox } from '../text-box/text-box';

enum Status {
  Idle,
  InProgress,
  Success,
  Error,
}

type Form = {
  firstName: string;
  lastName: string;
  password: string;
  tosChecked: boolean;
};

type Props = {
  token: string;
  userCreatedViaRegisterCompany: boolean;
  /**
   *  Whether this invitation is completed through Zitadel, as reported by the token lookup. When
   *  it is, SkyMap never sees a password: the form collects only the name, and the person is sent
   *  to Zitadel's own page to choose one.
   */
  isZitadel: boolean;
};

/**
 * Form verifies both user created from /registercompany and "Invite user".
 * Consider one API endpoint when moving from API v0 to v1.
 */
const VerifyForm = (props: Props) => {
  const { t } = useTranslation();
  const { goToLoginPage } = useRouter();
  const [status, setStatus] = React.useState(Status.Idle);
  const [authenticationFlow, setAuthenticationFlow] = React.useState<
    AuthenticationFlowType | undefined
  >();
  const [termsOfUseAccepted, setTermsOfUseAccepted] = React.useState(false);
  const [domainPolicy, setDomainPolicy] = React.useState<DomainPolicy | null>(null);
  const [isLoadingDomainPolicy, setIsLoadingDomainPolicy] = React.useState(true);

  const {
    handleSubmit,
    register,
    control,
    getValues,
    watch,
    formState: { errors, isValidating },
  } = useForm<Form>({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      tosChecked: false,
    },
  });

  const passwordConstraints = usePasswordConstraints({ password: watch('password') });

  // Set status to Idle to remove any errors (Status.Error) when revalidating the form.
  React.useEffect(() => {
    if (isValidating) {
      setStatus(Status.Idle);
    }
  }, [isValidating]);

  React.useEffect(() => {
    const subscription = watch((values, { name }) => {
      if (name === 'tosChecked') {
        if (values.tosChecked === false) {
          setAuthenticationFlow(undefined);
        }
        setTermsOfUseAccepted(Boolean(values.tosChecked));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Fetch domain policy based on invitation token to determine if SSO is enforced
  React.useEffect(() => {
    const fetchDomainPolicy = async () => {
      try {
        setIsLoadingDomainPolicy(true);
        const authService = SkyMapAxiosServiceFactory.instance.createAuthServiceV1();
        const response = await authService.getDomainPolicy({
          query: { type: 'token', value: props.token },
        });
        setDomainPolicy(response.data);
      } catch (error) {
        // If fetching fails or token not found, allow password option
        setDomainPolicy(null);
      } finally {
        setIsLoadingDomainPolicy(false);
      }
    };

    void fetchDomainPolicy();
  }, [props.token]);

  const shouldHidePasswordOption =
    domainPolicy?.isEnforced && domainPolicy.policyType === 'SSO_REQUIRED';

  const verifyAccount = async () => {
    try {
      setStatus(Status.InProgress);

      // Safety guard: prevent password verification if SSO is enforced. It cannot apply on the
      // Zitadel path, which never collects or submits a password in the first place.
      if (!props.isZitadel && shouldHidePasswordOption) {
        setStatus(Status.Error);
        return;
      }

      // The backend refuses a password on the Zitadel path with a 400, so it has to be left off
      // the request rather than sent empty.
      const password = props.isZitadel ? undefined : getValues('password');

      let setPasswordUrl: string | undefined;

      if (props.userCreatedViaRegisterCompany) {
        const companyService = SkyMapAxiosServiceFactory.instance.createCompanyServiceV0();
        const response = await companyService.verifyCompanyAdmin({
          body: {
            password: password,
          },
          path: {
            token: props.token,
          },
        });
        setPasswordUrl = response.setPassword?.url;
      } else {
        const userService = SkyMapAxiosServiceFactory.instance.createUserServiceV0();
        const response = await userService.verifyRegistration({
          body: {
            firstName: getValues('firstName'),
            lastName: getValues('lastName'),
            password: password,
          },
          path: {
            token: props.token,
          },
        });
        setPasswordUrl = response.setPassword?.url;
      }

      // Present only while the person still has to choose a first password. Somebody who already
      // registered in another environment keeps the password they have and gets no link, so the
      // ordinary success view is the right ending for them.
      if (isDefined(setPasswordUrl)) {
        // Started before leaving, so that choosing a password finishes inside this environment's
        // own authorization request and returns the person here signed in, rather than on the
        // organisation-wide default redirect. Absent when it could not be started, which leaves the
        // link exactly as it came and the old ending intact.
        const requestId = await startInvitationAuthorizationRequest();

        window.location.assign(buildInvitationUrl(setPasswordUrl, requestId));
        return;
      }

      setStatus(Status.Success);
    } catch (err) {
      setStatus(Status.Error);
    }
  };

  const renderFirstAndLastName = () => (
    <>
      <LabelledContainer
        required={true}
        text={t('verifyAccount.form.firstName', { ns: 'components' })}
      >
        {(formElementId) => (
          <TextBox
            {...register('firstName', {
              required: true,
              validate: (value) => !isEmptyField(value),
            })}
            disabled={status === Status.InProgress}
            errorMessage={errors.firstName?.message}
            id={formElementId()}
            maxLength={255}
          />
        )}
      </LabelledContainer>

      <LabelledContainer
        required={true}
        text={t('verifyAccount.form.lastName', { ns: 'components' })}
      >
        {(formElementId) => (
          <TextBox
            {...register('lastName', {
              required: true,
              validate: (value) => !isEmptyField(value),
            })}
            disabled={status === Status.InProgress}
            errorMessage={errors.lastName?.message}
            id={formElementId()}
            maxLength={255}
          />
        )}
      </LabelledContainer>
    </>
  );

  const renderForm = () => {
    return (
      <Form
        onSubmit={(e) => {
          void handleSubmit(verifyAccount)(e);
        }}
      >
        <InfoBox bottomMargin={true} color="yellow">
          {t('verifyAccount.info', { ns: 'components' })}
        </InfoBox>

        <TermsOfServiceCheckBox disabled={status === Status.InProgress} formControl={control} />

        {props.isZitadel ? (
          /**
           *  The invitation already decided how this person signs in, so there is no
           *  authentication choice to offer and no password to collect. They fill in their name
           *  and are handed to Zitadel to choose a password.
           */
          <>
            {!props.userCreatedViaRegisterCompany && renderFirstAndLastName()}

            <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
              <Button
                color="primary"
                disabled={!termsOfUseAccepted}
                loading={status === Status.InProgress}
                type="submit"
                variant={'contained'}
              >
                {t('verifyAccount.submitFormButton', { ns: 'components' })}
              </Button>
            </Stack>
          </>
        ) : isLoadingDomainPolicy ? (
          <Stack direction="column" spacing={1}>
            <TextLoading />
            <TextLoading />
          </Stack>
        ) : !isDefined(authenticationFlow) ? (
          <ChooseAuthentication
            disabled={!termsOfUseAccepted}
            hidePasswordOption={shouldHidePasswordOption}
            token={props.token}
            onPasswordSelection={() => setAuthenticationFlow('PASSWORD')}
          />
        ) : (
          <>
            {!props.userCreatedViaRegisterCompany && renderFirstAndLastName()}
            <LabelledContainer
              required={true}
              text={t('verifyAccount.form.password', { ns: 'components' })}
            >
              {(formElementId) => (
                <>
                  <TextBox
                    {...register('password', {
                      required: true,
                      validate: () => passwordConstraints.isValid,
                    })}
                    disabled={status === Status.InProgress}
                    errorMessage={errors.password?.message}
                    id={formElementId()}
                    maxLength={255}
                    type="password"
                  />
                  {passwordConstraints.render()}
                </>
              )}
            </LabelledContainer>

            <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
              <Button
                color="primary"
                loading={status === Status.InProgress}
                type="submit"
                variant={'contained'}
              >
                {t('verifyAccount.submitFormButton', { ns: 'components' })}
              </Button>
            </Stack>
          </>
        )}

        {status === Status.Error && (
          <InfoBox color="red" leftIcon={{ icon: ['fad', 'exclamation'] }}>
            {t('unexpectedError', { ns: 'common' })}
          </InfoBox>
        )}
      </Form>
    );
  };

  const renderVerificationSuccessful = () => {
    return (
      <Stack direction="column" spacing={2}>
        <InfoBox color="green" leftIcon={{ icon: ['fad', 'info'] }}>
          {t('verifyAccount.verificationSuccessful', { ns: 'components' })}
        </InfoBox>

        <Button alignSelf="flex-end" color="primary" variant="contained" onClick={goToLoginPage}>
          {t('verifyAccount.goToLoginPageButton', { ns: 'components' })}
        </Button>
      </Stack>
    );
  };

  return status === Status.Success ? renderVerificationSuccessful() : renderForm();
};

// TODO: Investigate how to expose event handlers based on prop "as" in Stack component:
// TODO: <Stack as="form" onSubmit="...">...</Stack>
// TODO: For now, style form with inline flex CSS.
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { VerifyForm, Props as VerifyFormProps };
